import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { Snackbar, Alert, AppBar, Toolbar, Typography, Box, Badge, Tooltip, Button, Chip, IconButton, Collapse } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ProjectSidebar from '../sidebar/ProjectSidebar';
import ChatInterface from '../chat/ChatInterface';
import RightPanel from '../rightpanel/RightPanel';
import { artifactService } from '../../services/artifactService';
import { reportService } from '../../services/reportService';
import { findingService } from '../../services/findingService';
import { websocketService, ConnectionStatus } from '../../services/websocketService';
import { Artifact, Finding, ChatSession } from '../../types/api.types';

const LayoutContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: 'inherit',
  width: 'inherit',
  overflow: 'hidden',
  backgroundColor: '#fafafa',
  // Add pulse animation for session indicators
  '@keyframes pulse': {
    '0%': {
      opacity: 1,
    },
    '50%': {
      opacity: 0.5,
    },
    '100%': {
      opacity: 1,
    },
  },
}));

const Header = styled(AppBar)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  zIndex: theme.zIndex.drawer + 1,
}));

// Connection status indicator styling with pulse animation for reconnecting state
const ConnectionStatusIndicator = styled('div')<{ status: ConnectionStatus; usingFallback?: boolean }>(({ theme, status, usingFallback }) => {
  let backgroundColor = '#999'; // Default gray
  let animation = 'none';
  let boxShadow = 'none';
  
  switch (status) {
    case ConnectionStatus.CONNECTED:
      backgroundColor = '#4caf50'; // Green
      if (usingFallback) {
        backgroundColor = '#8bc34a'; // Light green for fallback mode
      }
      break;
    case ConnectionStatus.CONNECTING:
      backgroundColor = '#ff9800'; // Orange
      animation = 'pulse 1.5s infinite';
      break;
    case ConnectionStatus.RECONNECTING:
      backgroundColor = '#ff9800'; // Orange
      animation = 'pulse 1.5s infinite';
      break;
    case ConnectionStatus.ERROR:
      backgroundColor = '#f44336'; // Red
      break;
    case ConnectionStatus.DISCONNECTED:
    default:
      backgroundColor = '#999'; // Gray
  }
  
  return {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    backgroundColor,
    marginLeft: theme.spacing(1),
    transition: 'background-color 0.3s ease',
    cursor: 'pointer',
    boxShadow: status === ConnectionStatus.CONNECTED ? '0 0 5px rgba(76, 175, 80, 0.5)' : 'none',
    animation,
    '@keyframes pulse': {
      '0%': {
        boxShadow: '0 0 0 0 rgba(255, 152, 0, 0.7)'
      },
      '70%': {
        boxShadow: '0 0 0 6px rgba(255, 152, 0, 0)'
      },
      '100%': {
        boxShadow: '0 0 0 0 rgba(255, 152, 0, 0)'
      }
    },
    '&:hover': {
      transform: 'scale(1.2)',
      transition: 'transform 0.2s ease'
    }
  };
});

const MainContent = styled('div')(({ theme }) => ({
  display: 'flex',
  flex: 1,
  overflow: 'hidden',
  marginTop: 0, // Remove margin since header is now part of layout
}));

const SidebarContainer = styled('div')<{ collapsed: boolean }>(({ theme, collapsed }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: collapsed ? '0px' : '280px',
  minWidth: collapsed ? '0px' : '280px',
  overflow: 'hidden',
  transition: 'width 0.3s ease, min-width 0.3s ease',
  borderRight: collapsed ? 'none' : `1px solid #e0e0e0`,
}));

const MainLayout: React.FC = () => {
  // State for selected project
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  
  // State for right panel width
  const [rightPanelWidth, setRightPanelWidth] = useState<number>(300);
  
  // State for triggering report refresh in RightPanel
  const [reportRefreshTrigger, setReportRefreshTrigger] = useState<number>(0);
  
  // State for sidebar collapse
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);
  
  // Handler for project selection with validation
  const handleProjectSelect = (projectId: string | null) => {
    console.log('MainLayout: Project selected:', projectId);
    if (projectId && typeof projectId === 'string') {
      const idRegex = /^[a-zA-Z0-9_-]+$/;
      if (!idRegex.test(projectId)) {
        console.error('Invalid project ID format:', projectId);
      }
    }
    setSelectedProject(projectId);
  };

  // Handler for sidebar toggle
  const toggleSidebar = () => {
    setSidebarCollapsed(prev => !prev);
  };
  
  // State for artifacts and findings
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [findings, setFindings] = useState<Finding[]>([]);
  const [reports, setReports] = useState<Finding[]>([]); // Reports converted to Finding format for UI compatibility
  
  // State for tracking new findings
  const [newFindings, setNewFindings] = useState<Finding[]>([]);
  const [lastViewedTimestamp, setLastViewedTimestamp] = useState<Date>(new Date());
  const [lastFindingsPollingTimestamp, setLastFindingsPollingTimestamp] = useState<Date>(new Date());
  const [newFindingsCount, setNewFindingsCount] = useState<number>(0);
  
  // State for tracking new artifacts
  const [newArtifacts, setNewArtifacts] = useState<Artifact[]>([]);
  const [lastArtifactViewedTimestamp, setLastArtifactViewedTimestamp] = useState<Date>(new Date());
  const [lastArtifactsPollingTimestamp, setLastArtifactsPollingTimestamp] = useState<Date>(new Date());
  const [newArtifactsCount, setNewArtifactsCount] = useState<number>(0);
  
  // State for tracking reports
  const [reportCount, setReportCount] = useState<number>(0);
  const [lastReportPollingTimestamp, setLastReportPollingTimestamp] = useState<Date>(new Date());
  
  const [report, setReport] = useState<any>(null);
  
  // State for error messages
  const [error, setError] = useState<string | null>(null);
  
  // State for notifications queue
  const [notifications, setNotifications] = useState<Array<{
    id: string;
    type: 'artifacts' | 'findings' | 'reports';
    count: number;
    message: string;
  }>>([]);
  const [currentNotification, setCurrentNotification] = useState<{
    id: string;
    type: 'artifacts' | 'findings' | 'reports';
    count: number;
    message: string;
  } | null>(null);
  
  // Load artifacts and findings when project changes
  useEffect(() => {
    if (selectedProject) {
      fetchArtifacts();
      fetchFindings();
      fetchReports(); // Fetch reports directly instead of checkForNewReports
      
      // Reset new findings tracking when project changes
      setNewFindings([]);
      setLastViewedTimestamp(new Date());
      setLastFindingsPollingTimestamp(new Date());
      setNewFindingsCount(0);
      
      // Reset new artifacts tracking when project changes
      setLastArtifactViewedTimestamp(new Date());
      setLastArtifactsPollingTimestamp(new Date());
      setNewArtifactsCount(0);
      
      // Reset reports tracking when project changes
      setReportCount(0);
      setReports([]);
      setLastReportPollingTimestamp(new Date());
    } else {
      // Clear artifacts and findings when no project is selected
      setArtifacts([]);
      setFindings([]);
      setNewFindings([]);
      setNewFindingsCount(0);
      setNewArtifactsCount(0);
      setReportCount(0);
      setReports([]);
    }
  }, [selectedProject]);
  
  // WebSocket connection status
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>(ConnectionStatus.DISCONNECTED);
  const [reconnectAttempt, setReconnectAttempt] = useState<number>(0);
  const [pollingFallbackActive, setPollingFallbackActive] = useState<boolean>(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Set up WebSocket connection for real-time updates
  useEffect(() => {
  // Clean up existing connections first
  websocketService.disconnect();
  
  // Clear any polling fallback
  if (pollingIntervalRef.current) {
    clearInterval(pollingIntervalRef.current);
    pollingIntervalRef.current = null;
  }
  setPollingFallbackActive(false);
  
  if (selectedProject) {
    // Small delay to ensure clean disconnection (reduced to 100ms for faster switching)
    setTimeout(() => {
      // Define WebSocket callbacks
      const callbacks = {
        onArtifactCreated: (artifact: Artifact) => {
          console.log('MainLayout: WebSocket event - New artifact created:', artifact);
          console.log('MainLayout: Artifact data received:', JSON.stringify(artifact, null, 2));
          console.log('MainLayout: Current artifacts count before update:', artifacts.length);
          
          setArtifacts(prevArtifacts => {
            // Check if this artifact is already in the list
            const exists = prevArtifacts.some(a => a.id === artifact.id);
            console.log('MainLayout: Artifact already exists?', exists);
            
            if (exists) {
              console.log('MainLayout: Artifact already exists, not adding duplicate');
              return prevArtifacts;
            }
            
            // Add the new artifact to the list
            const updatedArtifacts = [...prevArtifacts, artifact];
            console.log('MainLayout: Added new artifact, total count now:', updatedArtifacts.length);
            
            // Check if this is a new artifact (created after last viewed)
            const artifactDate = new Date(artifact.created_at);
            if (artifactDate > lastArtifactViewedTimestamp) {
              console.log('MainLayout: This is a new artifact, adding to notifications');
              setNewArtifacts(prev => [...prev, artifact]);
              setNewArtifactsCount(prev => prev + 1);
              
              // Add notification for new artifact
              addNotification('artifacts', 1, `New artifact added: ${artifact.name || 'Unnamed artifact'}`);
            }
            
            return updatedArtifacts;
          });
        },
        
        onArtifactDeleted: (data: { artifact_id: string; project_id: string }) => {
          console.log('Artifact deleted:', data.artifact_id);
          setArtifacts(prevArtifacts => 
            prevArtifacts.filter(a => a.id !== data.artifact_id)
          );
        },
        
        onArtifactFileUploaded: (data: any) => {
          console.log('File uploaded to artifact:', data);
          // Update the specific artifact in the list
          setArtifacts(prevArtifacts => prevArtifacts.map(artifact => 
            artifact.id === data.artifact_id ? {
              ...artifact,
              has_file: true,
              file_name: data.file_name,
              file_type: data.file_type
            } : artifact
          ));
        },
        
        onArtifactProcessingCompleted: (data: any) => {
          console.log('Artifact processing completed:', data);
          // Update the specific artifact in the list
          setArtifacts(prevArtifacts => prevArtifacts.map(artifact => 
            artifact.id === data.artifact_id ? {
              ...artifact,
              processing_status: data.status,
              processing_summary: data.summary
            } : artifact
          ));
        },
        
        onFindingCreated: (finding: Finding) => {
          console.log('🔍 MainLayout: WebSocket event - New finding created:', finding);
          console.log('🔍 MainLayout: Finding data received:', JSON.stringify(finding, null, 2));
          console.log('🔍 MainLayout: Current findings count before update:', findings.length);
          
          setFindings(prevFindings => {
            // Check if this finding is already in the list
            const exists = prevFindings.some(f => f.id === finding.id);
            console.log('🔍 MainLayout: Finding already exists?', exists);
            
            if (exists) {
              console.log('🔍 MainLayout: Finding already exists, not adding duplicate');
              return prevFindings;
            }
            
            // Add the new finding to the list
            const updatedFindings = [...prevFindings, finding];
            console.log('🔍 MainLayout: Added new finding, total count now:', updatedFindings.length);
            
            // Check if this is a new finding (created after last viewed)
            const findingDate = new Date(finding.created_at);
            if (findingDate > lastViewedTimestamp) {
              console.log('🔍 MainLayout: This is a new finding, adding to notifications');
              setNewFindings(prev => [...prev, finding]);
              setNewFindingsCount(prev => prev + 1);
              
              // Add notification for new analysis with severity
              const severity = finding.severity || 'unknown';
              addNotification('findings', 1, `New ${severity} analysis: ${finding.name || 'Unnamed analysis'}`);
            }
            
            return updatedFindings;
          });
        },
        
        onFindingDeleted: (data: { finding_id: string; project_id: string }) => {
          console.log('Finding deleted:', data.finding_id);
          setFindings(prevFindings => 
            prevFindings.filter(f => f.id !== data.finding_id)
          );
        },
        
        onFindingFileUploaded: (data: any) => {
          console.log('File uploaded to finding:', data);
          // Update finding in the list if needed
          fetchFindings(true);
        },

        onReportGenerated: (report: any) => {
          console.log('🎉 MainLayout: WebSocket event - Report generated:', report);
          console.log('🎉 MainLayout: Report ID:', report.id);
          console.log('🎉 MainLayout: Report status:', report.status);
          console.log('🎉 MainLayout: Full report data:', JSON.stringify(report, null, 2));
          
          // Stop aggressive polling since report is complete (use ref to avoid stale closure)
          if (isGeneratingReportRef.current) {
            console.log('🎉 MainLayout: Report completed, stopping aggressive polling');
            setIsGeneratingReport(false);
          }
          
          // Immediately fetch reports to update the state
          console.log('🎉 MainLayout: Fetching reports after report_generated event');
          fetchReports(true);
          
          // Also trigger a delayed second fetch in case the first one is too fast
          setTimeout(() => {
            console.log('🎉 MainLayout: Delayed report fetch (1 second after event)');
            fetchReports(true);
          }, 1000);
          
          // Add notification for new report
          addNotification('reports', 1, `Report "${report.title || 'Untitled Report'}" is ready`);
          
          // Report completion notification - no chat message needed since notification handles it
        },
        
        onConnectionStatusChange: (status: ConnectionStatus) => {
          console.log('MainLayout: WebSocket connection status changed:', status);
          setConnectionStatus(status);
          
          // Reset reconnection counter when connected
          if (status === ConnectionStatus.CONNECTED) {
            setReconnectAttempt(0);
            fetchFindings(true);
            fetchArtifacts(true);
            checkForNewReports(true);
            
            // Disable polling fallback if it was active
            if (pollingFallbackActive) {
              setPollingFallbackActive(false);
              
              // Clear any existing polling interval
              if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
              }
              
              // Show success message when reconnected
              setError('WebSocket connection restored. Real-time updates are now active.');
              
              // Auto-hide after 3 seconds
              setTimeout(() => {
                setError(null);
              }, 3000);
            }
          } else if (status === ConnectionStatus.RECONNECTING) {
            setReconnectAttempt(prev => prev + 1);
            
            // Show a temporary message about reconnection attempt
            setError(`Reconnecting to server (attempt ${reconnectAttempt + 1})...`);
          }
        },
        
        onError: (error: any) => {
          console.error('WebSocket error:', error);
          
          // Handle fallback messaging
          if (error.fallback) {
            console.log('Using fallback mechanism:', error.fallback);
            
            // If the fallback mentions polling, set up the polling fallback
            if (error.fallback.toLowerCase().includes('polling') && !pollingFallbackActive) {
              setPollingFallbackActive(true);
              
              // Set up the polling fallback for project updates
              if (selectedProject) {
                pollingIntervalRef.current = websocketService.setupPollingFallback(selectedProject);
                
                // Show message about fallback
                setError(`Using polling fallback for updates. ${error.fallback}`);
                
                // Auto-hide after 5 seconds
                setTimeout(() => {
                  setError(null);
                }, 5000);
              }
            } else {
              // For other fallbacks, just show the message
              setError(`Using alternative update method. ${error.fallback}`);
              
              // Auto-hide after 5 seconds
              setTimeout(() => {
                setError(null);
              }, 5000);
            }
          } else {
            // For actual errors, show the error message with more context
            let errorMessage = `Connection error: ${error.detail || 'Unknown error'}`;
            
            // Add helpful context based on error type
            if (error.code === 1006) {
              errorMessage += '. The server closed the connection unexpectedly.';
            } else if (error.code === 1001) {
              errorMessage += '. The server is going away.';
            } else if (error.status === 403) {
              errorMessage += '. Access denied - please reload the page.';
            }
            
            setError(errorMessage);
          }
        }
      };
      
      console.log('🔍 MainLayout: Setting up WebSocket callbacks');
      console.log('🔍 MainLayout: onFindingCreated callback registered:', !!callbacks.onFindingCreated);
      
      // Connect to the project WebSocket
      console.log('🔍 MainLayout: Connecting to WebSocket for project:', selectedProject);
      websocketService.connectToProject(selectedProject, callbacks);
      
      // Initial data fetch
      fetchFindings();
      fetchArtifacts();
      checkForNewReports();
    }, 100); // Reduced from 500ms to 100ms for faster project switching
  } else {
    // Disconnect WebSocket when no project is selected
    websocketService.disconnect();

    // Clear any polling fallback
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setPollingFallbackActive(false);
  }
    
  return () => {
      // Clean up WebSocket connection and polling on unmount or when project changes
      websocketService.disconnect();
      
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [selectedProject]);
  
  // Function moved and merged with the existing addNotification function below
  
  // Fetch artifacts for the selected project
  const fetchArtifacts = async (silent: boolean = false) => {
    if (!selectedProject) return;
    
    try {
      const response = await artifactService.getArtifacts(selectedProject);
      setArtifacts(response);
      
      // Check for new artifacts since last viewed (for UI badge count)
      if (response.length > 0) {
        const currentLastViewedTime = lastArtifactViewedTimestamp;
        
        // Filter artifacts created after last viewed timestamp
        const newlyAddedArtifacts = response.filter(artifact => {
          const artifactDate = new Date(artifact.created_at);
          return artifactDate > currentLastViewedTime;
        });
        
        // Update new artifacts count for UI
        if (newlyAddedArtifacts.length > 0) {
          setNewArtifacts(newlyAddedArtifacts);
          setNewArtifactsCount(newlyAddedArtifacts.length);
        }
        
        // Update polling timestamp for state tracking
        if (silent) {
          setLastArtifactsPollingTimestamp(new Date());
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch artifacts:', err);
      
      // Check if this is an authentication error
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        console.log('MainLayout: Authentication failed when fetching artifacts, session may have expired');
        // Trigger session recovery
        setError('Session expired. Please refresh the page or restart your session.');
        return;
      }
      
      if (!silent) {
        setError('Failed to load artifacts. Please try again later.');
      }
    }
  };
  
  // Add periodic refresh as fallback when WebSocket may be missing messages
  useEffect(() => {
    let refreshInterval: NodeJS.Timeout | null = null;
    
    // Set up more aggressive fallback polling for long-running operations
    if (selectedProject) {
      // Poll more frequently to catch scan results that might be missed by WebSocket
      refreshInterval = setInterval(() => {
        console.log('MainLayout: Performing enhanced fallback refresh of artifacts and findings');
        fetchArtifacts(true);
        fetchFindings(true);
      }, 8000); // Reduced to 8 seconds for better scan result catching
    }
    
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [selectedProject]); // Removed connectionStatus dependency to always poll

  // Additional aggressive polling specifically for findings during potential scan periods
  useEffect(() => {
    let aggressiveRefreshInterval: NodeJS.Timeout | null = null;
    
    if (selectedProject && artifacts.length > 0) {
      // If we have artifacts, we might have ongoing scans that will produce findings
      // Poll findings more aggressively for 5 minutes after last artifact change
      aggressiveRefreshInterval = setInterval(() => {
        console.log('MainLayout: Aggressive findings refresh (scan result catching)');
        fetchFindings(true);
      }, 5000); // Every 5 seconds
      
      // Stop aggressive polling after 5 minutes
      setTimeout(() => {
        if (aggressiveRefreshInterval) {
          clearInterval(aggressiveRefreshInterval);
          console.log('MainLayout: Stopping aggressive findings polling');
        }
      }, 300000); // 5 minutes
    }
    
    return () => {
      if (aggressiveRefreshInterval) {
        clearInterval(aggressiveRefreshInterval);
      }
    };
  }, [selectedProject, artifacts.length]); // Trigger when artifacts change

  // Additional aggressive polling for reports after generation request
  const [isGeneratingReport, setIsGeneratingReport] = useState<boolean>(false);
  const reportPollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isGeneratingReportRef = useRef<boolean>(false);
  
  // Keep ref in sync with state
  useEffect(() => {
    isGeneratingReportRef.current = isGeneratingReport;
  }, [isGeneratingReport]);
  
  useEffect(() => {
    let reportPollingInterval: NodeJS.Timeout | null = null;
    
    if (selectedProject && isGeneratingReport) {
      console.log('MainLayout: Starting aggressive report polling (every 3 seconds)');
      
      // Poll reports aggressively while report is being generated
      reportPollingInterval = setInterval(() => {
        console.log('MainLayout: Aggressive report polling...');
        fetchReports(true);
      }, 3000); // Every 3 seconds
      
      // Stop aggressive polling after 5 minutes (reports should be done by then)
      // Use ref to avoid creating multiple timeouts
      if (reportPollingTimeoutRef.current) {
        clearTimeout(reportPollingTimeoutRef.current);
      }
      
      reportPollingTimeoutRef.current = setTimeout(() => {
        console.log('MainLayout: Stopping aggressive report polling after timeout');
        setIsGeneratingReport(false);
      }, 300000); // 5 minutes
    }
    
    return () => {
      if (reportPollingInterval) {
        clearInterval(reportPollingInterval);
      }
      if (reportPollingTimeoutRef.current) {
        clearTimeout(reportPollingTimeoutRef.current);
        reportPollingTimeoutRef.current = null;
      }
    };
  }, [selectedProject, isGeneratingReport]);
  
  // Fetch findings for the selected project
  const fetchFindings = async (silent: boolean = false) => {
    if (!selectedProject) return;
    
    console.log(`🔍 fetchFindings called - silent: ${silent}, project: ${selectedProject}`);
    
    try {
      const response = await findingService.getFindings(selectedProject);
      console.log(`🔍 fetchFindings: API returned ${response.length} findings`);
      console.log('🔍 fetchFindings: Response data:', response);
      
      // Set all findings
      setFindings(response);
      console.log(`🔍 fetchFindings: Updated findings state with ${response.length} items`);
      
      // Check for new findings since last viewed (for UI badge count)
      if (response.length > 0) {
        const currentLastViewedTime = lastViewedTimestamp;
        console.log('🔍 fetchFindings: Last viewed timestamp:', currentLastViewedTime);
        
        // Filter findings created after last viewed timestamp
        const newlyAddedFindings = response.filter((finding: any) => {
          const findingDate = new Date(finding.created_at);
          return findingDate > currentLastViewedTime;
        });
        
        console.log(`🔍 fetchFindings: Found ${newlyAddedFindings.length} new findings since last viewed`);
        
        // Update new findings count for UI
        if (newlyAddedFindings.length > 0) {
          setNewFindings(newlyAddedFindings);
          setNewFindingsCount(newlyAddedFindings.length);
          console.log(`🔍 fetchFindings: Updated new findings count to ${newlyAddedFindings.length}`);
        }
        
        // Update polling timestamp for state tracking
        if (silent) {
          setLastFindingsPollingTimestamp(new Date());
          console.log('🔍 fetchFindings: Updated polling timestamp');
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch findings:', err);
      
      // Check if this is an authentication error
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        console.log('MainLayout: Authentication failed when fetching findings, session may have expired');
        // Trigger session recovery
        setError('Session expired. Please refresh the page or restart your session.');
        return;
      }
      
      if (!silent) {
        setError('Failed to load analysis items. Please try again later.');
      }
    }
  };

  // Fetch reports function similar to fetchFindings and fetchArtifacts
  const fetchReports = async (silent: boolean = false) => {
    if (!selectedProject) return;
    
    console.log(`📊 fetchReports called - silent: ${silent}, project: ${selectedProject}`);
    
    try {
      const reportsData = await reportService.getReports(selectedProject);
      console.log(`📊 fetchReports: API returned ${reportsData.length} reports`);
      console.log('📊 fetchReports: Response data:', reportsData);
      
      if (reportsData && reportsData.length > 0) {
        // Convert to Finding format for compatibility with UI
        const reportFindings = reportsData.map(report => ({
          id: report.id,
          name: report.title || "Project Report",
          description: `Generated: ${new Date(report.created_at).toLocaleString()}`,
          severity: "medium" as 'medium', // Type assertion to satisfy the union type
          project_id: report.project_id,
          created_at: report.created_at,
          has_file: true,
          file_name: "report.md",
          file_type: "markdown",
          type: "report",
          processing_status: report.status || 'completed'  // Map backend 'status' to frontend 'processing_status'
        }));
        
        console.log('📊 fetchReports: Mapped reports with status:', reportFindings.map(r => ({
          id: r.id,
          name: r.name,
          processing_status: (r as any).processing_status
        })));
        
        setReports(reportFindings);
        console.log(`📊 fetchReports: Updated reports state with ${reportFindings.length} items`);
      } else {
        setReports([]);
        console.log('📊 fetchReports: No reports found, clearing state');
      }
    } catch (err: any) {
      console.error('Failed to fetch reports:', err);
      
      // Check if this is an authentication error
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        console.log('MainLayout: Authentication failed when fetching reports, session may have expired');
        // Trigger session recovery
        setError('Session expired. Please refresh the page or restart your session.');
        return;
      }
      
      if (!silent) {
        setError('Failed to load reports. Please try again later.');
      }
    }
  };

  // Check for new reports
  const checkForNewReports = async (silent: boolean = false) => {
    if (!selectedProject) return;
    
    try {
      const reports = await reportService.getReports(selectedProject);
      
      if (reports && reports.length > 0) {
        console.log('MainLayout: Fetched reports from reports collection:', reports);
        // Convert to Finding format for compatibility with UI (same as RightPanel)
        const reportFindings = reports.map(report => ({
          id: report.id,
          name: report.title || "Project Report",
          description: `Generated: ${new Date(report.created_at).toLocaleString()}`,
          severity: "medium" as 'medium', // Type assertion to satisfy the union type
          project_id: report.project_id,
          created_at: report.created_at,
          has_file: true,
          file_name: "report.md",
          file_type: "markdown",
          type: "report",
          is_manual: false, // Reports are tool-generated
          processing_status: report.status || 'completed'  // Map backend 'status' to frontend 'processing_status'
        }));
        
        // Update reports state - this will trigger the state tracking useEffect
        setReports(reportFindings);
        
        // Keep report count for backward compatibility
        setReportCount(reportFindings.length);
      } else {
        console.log('MainLayout: No reports found in reports collection');
        setReports([]);
        setReportCount(0);
      }
      
      // Update polling timestamp for state tracking
      if (silent) {
        setLastReportPollingTimestamp(new Date());
      }
    } catch (err) {
      console.error('Failed to check for new reports:', err);
      // Don't show error for report checking as it's not critical
    }
  };

  // Function to trigger RightPanel report refresh
  const handleRefreshReports = () => {
    console.log('🔄 MainLayout: Triggering report refresh...');
    // Increment the trigger to force RightPanel to refresh its reports
    setReportRefreshTrigger(prev => prev + 1);
  };

  // Handler for adding artifacts
  const handleAddArtifact = async (artifactName: string, artifactType: string) => {
    if (!selectedProject) return;
    
    try {
      // Create artifact with is_manual flag - but don't update state here
      // The WebSocket event handler will add it to the state
      await artifactService.createArtifact({
        name: artifactName,
        type: artifactType,
        project_id: selectedProject,
        is_manual: true
      });
      
    } catch (err) {
      console.error('Failed to create artifact:', err);
      setError('Failed to create artifact. Please try again.');
    }
  };

  // Handler for adding findings
  const handleAddFinding = async (findingName: string) => {
    if (!selectedProject) return;
    
    try {
      const newFinding = await findingService.createFinding({
        name: findingName,
        description: '',
        severity: 'medium',
        project_id: selectedProject,
        is_manual: true, // Mark as manually created finding
      });
      console.log('Successfully created analysis item');
      
      setFindings([...findings, newFinding]);
      
      // Notification will be triggered by state change useEffect
    } catch (err) {
      console.error('Failed to create analysis item:', err);
      setError('Failed to create analysis item. Please try again.');
    }
  };
  
  // Handle findings tab click - mark all findings as viewed
  // Handle findings tab click - memoized for performance
  const handleFindingsTabClick = useCallback(() => {
    // Update last viewed timestamp
    setLastViewedTimestamp(new Date());
    
    // Reset new findings count
    setNewFindingsCount(0);
    setNewFindings([]);
  }, []);
  
  // Handle artifacts tab click - mark all artifacts as viewed - memoized for performance
  const handleArtifactsTabClick = useCallback(() => {
    // Update last viewed timestamp
    setLastArtifactViewedTimestamp(new Date());
    
    // Reset new artifacts count
    setNewArtifactsCount(0);
    setNewArtifacts([]);
  }, []);
  
  // Notification queue system
  const addNotification = (type: 'artifacts' | 'findings' | 'reports', count: number, customMessage?: string) => {
    const id = `${type}-${Date.now()}`;
    let message = customMessage || '';
    
    // Generate default message if none provided
    if (!customMessage) {
      let total = 0;
      
      // Get the current total count for each type
      switch (type) {
        case 'artifacts':
          total = artifacts.length;
          message = `${count} Artifact${count > 1 ? 's' : ''} added to your project (${total} total)`;
          break;
        case 'findings':
          total = findings.length;
          message = `${count} Analysis${count > 1 ? 'es' : ''} discovered and added (${total} total)`;
          break;
        case 'reports':
          total = reports.length;
          message = `${count} Report${count > 1 ? 's' : ''} generated successfully (${total} total)`;
          break;
      }
    }
    
    const notification = { id, type, count, message };
    
    setNotifications(prev => [...prev, notification]);
  };
  
  // Process notification queue - show one at a time
  React.useEffect(() => {
    if (notifications.length > 0 && !currentNotification) {
      const nextNotification = notifications[0];
      setCurrentNotification(nextNotification);
      setNotifications(prev => prev.slice(1));
    }
  }, [notifications, currentNotification]);
  
  // Track artifacts state changes for notifications
  const prevArtifactsLength = useRef<number>(0);
  React.useEffect(() => {
    if (selectedProject && artifacts.length > 0) {
      const currentLength = artifacts.length;
      const previousLength = prevArtifactsLength.current;
      
      // Only show notification if artifacts were added (length increased)
      if (previousLength > 0 && currentLength > previousLength) {
        const newCount = currentLength - previousLength;
        // Use default generated message
        addNotification('artifacts', newCount);
      }
      
      // Update the ref for next comparison
      prevArtifactsLength.current = currentLength;
    }
  }, [artifacts, selectedProject]);
  
  // Track findings state changes for notifications
  const prevFindingsLength = useRef<number>(0);
  React.useEffect(() => {
    if (selectedProject && findings.length > 0) {
      const currentLength = findings.length;
      const previousLength = prevFindingsLength.current;
      
      // Only show notification if findings were added (length increased)
      if (previousLength > 0 && currentLength > previousLength) {
        const newCount = currentLength - previousLength;
        // Use default generated message
        addNotification('findings', newCount);
      }
      
      // Update the ref for next comparison
      prevFindingsLength.current = currentLength;
    }
  }, [findings, selectedProject]);
  
  // Track reports state changes for notifications
  const prevReportsLength = useRef<number>(0);
  React.useEffect(() => {
    if (selectedProject && reports.length > 0) {
      const currentLength = reports.length;
      const previousLength = prevReportsLength.current;
      
      // Only show notification if reports were added (length increased)
      if (previousLength > 0 && currentLength > previousLength) {
        const newCount = currentLength - previousLength;
        // Use default generated message
        addNotification('reports', newCount);
      }
      
      // Update the ref for next comparison
      prevReportsLength.current = currentLength;
    }
  }, [reports, selectedProject]);
  
  // Reset ref counters when project changes
  React.useEffect(() => {
    if (selectedProject) {
      // Reset counters when switching projects
      prevArtifactsLength.current = 0;
      prevFindingsLength.current = 0;
      prevReportsLength.current = 0;
    }
  }, [selectedProject]);
  
  // Handle notification close - memoized for performance
  const handleCloseNotification = useCallback(() => {
    setCurrentNotification(null);
  }, []);

  // Handle closing error messages - memoized for performance  
  const handleCloseError = useCallback(() => {
    setError(null);
  }, []);

  // Reference to chat interface for getting current session
  const chatInterfaceRef = useRef<any>(null);
  
  // Handle manual WebSocket reconnection
  const handleReconnectWebSocket = () => {
    if (selectedProject) {
      // Reset reconnection counter and update UI
      setReconnectAttempt(0);
      setError('Attempting to reconnect to server...');
      
      // Stop polling fallback if active
      if (pollingFallbackActive && pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        setPollingFallbackActive(false);
      }
      
      // Reconnect WebSocket
      websocketService.reconnectToProject();
    }
  };

  // Handle report generation request from right panel
  const handleGenerateReport = async (projectId: string) => {
    try {
      // Start aggressive polling for reports
      setIsGeneratingReport(true);
      
      // Show notification instead of chat message
      addNotification('reports', 1, 'Report generation has been requested. The report will be available in the Reports tab when ready.');

      // Get the current chat session from the ChatInterface component
      // With the simplified one-session-per-project design, we always have a session
      const currentSession = chatInterfaceRef.current?.getCurrentSession();
      
      // Generate report using the default title and options
      const result = await reportService.generateReport(
        projectId,
        "Penetration Test Report",
        true, // includeArtifacts
        true, // includeFindings
        undefined // query
      );
      
      // Immediately fetch reports after API call
      console.log('📊 MainLayout: Report generation API call completed, fetching reports immediately');
      fetchReports(true);
      
      // Also schedule additional fetches
      setTimeout(() => fetchReports(true), 2000);  // After 2 seconds
      setTimeout(() => fetchReports(true), 5000);  // After 5 seconds
      setTimeout(() => fetchReports(true), 10000); // After 10 seconds
      
      return true;
    } catch (error) {
      console.error('Error generating report from MainLayout:', error);
      setIsGeneratingReport(false); // Stop aggressive polling on error
      return false;
    }
  };

  return (
    <LayoutContainer>
      <Header position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="toggle sidebar"
            onClick={toggleSidebar}
            sx={{ mr: 2 }}
          >
            {sidebarCollapsed ? <MenuIcon /> : <ChevronLeftIcon />}
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            PenTest Application
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="subtitle2" sx={{ opacity: 0.8, fontStyle: 'italic', mr: 1 }}>
              SecAI
            </Typography>
            {pollingFallbackActive && (
              <Tooltip title="Using polling fallback instead of WebSockets">
                <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.7rem', backgroundColor: 'rgba(255,152,0,0.2)', px: 0.5, py: 0.2, borderRadius: 1 }}>
                  FALLBACK MODE
                </Typography>
              </Tooltip>
            )}
          </Box>
        </Toolbar>
      </Header>
      
      <MainContent>
        <SidebarContainer collapsed={sidebarCollapsed}>
          <Collapse in={!sidebarCollapsed} orientation="horizontal">
            <ProjectSidebar 
              selectedProject={selectedProject}
              setSelectedProject={handleProjectSelect}
            />
          </Collapse>
        </SidebarContainer>
        
        <ChatInterface 
          selectedProject={selectedProject} 
          ref={chatInterfaceRef}
          rightPanelWidth={rightPanelWidth}
        />
        <RightPanel 
          artifacts={artifacts}
          findings={findings}
          reportsData={reports}
          onAddArtifact={handleAddArtifact}
          onAddFinding={handleAddFinding}
          newFindingsCount={newFindingsCount}
          newArtifactsCount={newArtifactsCount}
          onFindingsTabClick={handleFindingsTabClick}
          onArtifactsTabClick={handleArtifactsTabClick}
          onRefreshFindings={() => fetchFindings(true)}
          onRefreshArtifacts={() => fetchArtifacts(true)}
          onRefreshReports={() => fetchReports(true)}
          reportRefreshTrigger={reportRefreshTrigger}
          report={report}
          projectId={selectedProject || ''}
          onGenerateReport={handleGenerateReport}
          width={rightPanelWidth}
          onWidthChange={setRightPanelWidth}
        />
      </MainContent>
      
      {/* Error Snackbar */}
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={handleCloseError}
        action={
          connectionStatus === ConnectionStatus.ERROR || connectionStatus === ConnectionStatus.DISCONNECTED ? (
            <Button color="secondary" size="small" onClick={handleReconnectWebSocket}>
              Reconnect
            </Button>
          ) : null
        }
      >
        <Alert 
          onClose={handleCloseError} 
          severity={
            error && error.includes('restored') ? "success" : 
            error && error.includes('Reconnecting') ? "info" : 
            error && error.includes('fallback') || error && error.includes('alternative') ? "warning" : 
            "error"
          }
          sx={{ width: '100%' }}
        >
          {error}
          {pollingFallbackActive && !error?.includes('fallback') && (
            <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
              Using polling fallback for updates.
            </Typography>
          )}
        </Alert>
      </Snackbar>
      
      {/* Success Notification Snackbar */}
      <Snackbar 
        open={!!currentNotification} 
        autoHideDuration={4000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }} // Changed to top-right
        sx={{
          marginTop: '64px' // Add margin to avoid overlapping with the header
        }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity="success" 
          sx={{ 
            width: '100%',
            backgroundColor: '#2e7d32', // Standardized green color for all types
            color: 'white',
            '& .MuiAlert-icon': {
              color: 'white'
            }
          }}
        >
          {currentNotification?.message}
        </Alert>
      </Snackbar>
    </LayoutContainer>
  );
};

export default MainLayout;
